import React, { useEffect } from 'react';
import {
  Title,
  Text,
  Label,
  Select,
  Option,
  CheckBox,
  Button,
  Input,
  MessageStrip,
  Icon,
  ObjectStatus,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function NotificationsConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const notificationSettings = systemSettings?.DELIVERY_NOTIFICATIONS;
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    console.log('NotificationsConfig - Current Values:', { formData, notificationSettings });
  }, [formData, notificationSettings]);

  if (!formData || !notificationSettings) {
    return <div>Error: Faltan datos necesarios para la configuración de notificaciones</div>;
  }

  const handleInputChange = (field, value) => {
    if (field === 'cargoType') {
      const cargoTypeSettings = notificationSettings.attachments.cargoTypeRequirements[value];
      setFormData({
        ...formData,
        cargoType: value,
        requiredAttachments: [...cargoTypeSettings.requiredAttachments],
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleAttachmentChange = (attachmentId) => {
    const cargoTypeSettings =
      notificationSettings.attachments.cargoTypeRequirements[formData.cargoType];
    const isRequired = cargoTypeSettings.requiredAttachments.includes(attachmentId);
    const isOptional = cargoTypeSettings.optionalAttachments.includes(attachmentId);

    if (!isRequired && !isOptional) return;
    if (isRequired) return;

    const updatedAttachments = formData.requiredAttachments.includes(attachmentId)
      ? formData.requiredAttachments.filter(a => a !== attachmentId)
      : [...formData.requiredAttachments, attachmentId];

    setFormData({ ...formData, requiredAttachments: updatedAttachments });
  };

  const handleStatusLabelChange = (key, value) => {
    setFormData({
      ...formData,
      statusLabels: { ...formData.statusLabels, [key]: value },
    });
  };

  const prepareDataForSave = () => ({
    ...notificationSettings,
    defaultValues: {
      ...notificationSettings.defaultValues,
      selectedCargoType: formData.cargoType,
      requiredAttachments: formData.requiredAttachments,
      statusLabels: formData.statusLabels,
    },
  });

  const handleSave = () => onSave(prepareDataForSave());

  const hasChanges = () => {
    const dv = notificationSettings.defaultValues;
    return (
      formData.cargoType !== dv.selectedCargoType ||
      JSON.stringify(formData.requiredAttachments) !== JSON.stringify(dv.requiredAttachments) ||
      JSON.stringify(formData.statusLabels) !== JSON.stringify(dv.statusLabels)
    );
  };

  const isAttachmentRequired = (attachmentId) => {
    const ct = notificationSettings.attachments.cargoTypeRequirements[formData.cargoType];
    return ct.requiredAttachments.includes(attachmentId);
  };

  const isAttachmentAvailable = (attachmentId) => {
    const ct = notificationSettings.attachments.cargoTypeRequirements[formData.cargoType];
    return (
      ct.requiredAttachments.includes(attachmentId) ||
      ct.optionalAttachments.includes(attachmentId)
    );
  };

  const attachmentStatus = () => {
    const required = notificationSettings.attachments.minimumRequired;
    const current = formData.requiredAttachments.length;
    return { required, current, state: current >= required ? 'Positive' : 'Critical' };
  };

  const renderSelect = (field, options, currentValue) => (
    <Select
      value={currentValue}
      style={{ width: '100%' }}
      onChange={(e) => {
        const selectedValue =
          e.detail.selectedOption.getAttribute('data-key') || e.detail.selectedOption.value;
        handleInputChange(field, selectedValue);
      }}
    >
      {options.map(option => (
        <Option key={option.value} data-key={option.value} selected={option.value === currentValue}>
          {option.label}
        </Option>
      ))}
    </Select>
  );

  const { required, current, state } = attachmentStatus();

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">{autoT('Configuración de Notificaciones de Entrega')}</Title>
          <Text style={CONFIG_STYLES.labelHint}>
            {autoT('Configure los adjuntos obligatorios para cada tipo de carga y personalice los estados de entrega.')}
          </Text>
        </div>
        <Button
          design="Emphasized"
          onClick={handleSave}
          disabled={!hasChanges() || isLoading}
          icon="save"
        >
          {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
        </Button>
      </div>

      {hasChanges() && (
        <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
          {autoT('Hay cambios pendientes de guardar')}
        </MessageStrip>
      )}

      {/* Tipo de carga */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Tipo de Carga')}</Title>
        </div>
        <div style={{ maxWidth: '380px' }}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Tipo de Carga')}</Label>
            {renderSelect('cargoType', notificationSettings.cargoTypes.options, formData.cargoType)}
          </div>
        </div>
      </div>

      {/* Adjuntos requeridos */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Adjuntos de Notificación según Tipo de Carga')}</Title>
        </div>
        <Text style={{ ...CONFIG_STYLES.labelHint, display: 'block', marginBottom: '0.75rem' }}>
          {autoT('Configuración para:')} {notificationSettings.cargoTypes.options.find(o => o.value === formData.cargoType)?.label}
        </Text>

        <div style={{ marginBottom: '1rem' }}>
          <ObjectStatus state={state} showDefaultIcon>
            {autoT('Adjuntos seleccionados:')} {current} / {autoT('Mínimo requerido:')} {required}
          </ObjectStatus>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.5rem' }}>
          {notificationSettings.attachments.options.map(attachment => {
            const available = isAttachmentAvailable(attachment.id);
            const required  = isAttachmentRequired(attachment.id);
            return (
              <CheckBox
                key={attachment.id}
                text={`${attachment.name}${required ? ` (${autoT('Requerido')})` : ''}`}
                checked={formData.requiredAttachments.includes(attachment.id)}
                onChange={() => handleAttachmentChange(attachment.id)}
                disabled={!available || required}
              />
            );
          })}
        </div>
      </div>

      {/* Estados de entrega */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Estados de Entrega')}</Title>
        </div>
        <Text style={{ ...CONFIG_STYLES.labelHint, display: 'block', marginBottom: '1rem' }}>
          {autoT('Personalice los nombres de los estados de entrega que se mostrarán en el sistema.')}
        </Text>
        <div style={CONFIG_STYLES.formGrid}>
          {notificationSettings.deliveryStatuses.options.map(status => (
            <div key={status.value} style={CONFIG_STYLES.fieldGroup}>
              <Label>{status.originalLabel}</Label>
              <Input
                value={formData.statusLabels[status.value] || status.customLabel}
                style={{ width: '100%' }}
                onChange={(e) => handleStatusLabelChange(status.value, e.target.value)}
                placeholder={status.customLabel}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Resumen */}
      <div style={CONFIG_STYLES.infoBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="detail-view" style={{ color: 'var(--sapHighlightColor)' }} />
          <Title level="H5">{autoT('Resumen de Configuraciones')}</Title>
        </div>
        <div style={{
          background: 'var(--sapBackgroundColor)',
          padding: '1rem',
          borderRadius: '0.375rem',
        }}>
          <Text>
            <strong>
              {notificationSettings.cargoTypes.options.find(o => o.value === formData.cargoType)?.label}
            </strong>
          </Text>
          <br />
          <Text style={CONFIG_STYLES.labelHint}>
            {autoT('Adjuntos seleccionados:')} {formData.requiredAttachments.length}
          </Text>
          <br />
          <Text style={CONFIG_STYLES.labelHint}>
            {autoT('Adjuntos requeridos:')}{' '}
            {notificationSettings.attachments.cargoTypeRequirements[formData.cargoType].requiredAttachments.length}
          </Text>
          <br />
          <Text style={CONFIG_STYLES.labelHint}>
            {autoT('Adjuntos opcionales:')}{' '}
            {notificationSettings.attachments.cargoTypeRequirements[formData.cargoType].optionalAttachments.length}
          </Text>
        </div>
      </div>
    </div>
  );
}

export default NotificationsConfig;
